import { useEffect } from 'react';

import { useAppSelector } from 'src/redux';

import { positionsSelectors } from 'src/redux/positions';
import { userSelectors } from 'src/redux/user';
import { isStatusFailed } from 'src/utils';

import {
  consumePendingApplicationEvent,
  consumePendingLoginEvent,
  pushApplicationEvent,
  pushLoginEvent,
  pushUserIdEvent,
  setUser,
} from 'src/services';

export const useAnalytics = () => {
  const userData = useAppSelector(userSelectors.getUserData);
  const positionData = useAppSelector(positionsSelectors.getPositionData);
  const positionsApiStatus = useAppSelector(
    positionsSelectors.getPositionsApiStatus,
  );

  const positionId =
    userData?.positions?.[0]?.positionId ??
    positionData?.positionId ??
    userData?.matchedJobOpenings?.[0] ??
    null;

  // An apply-link position is still being applied (usePositionData) — defer
  // the identity/login pushes so they carry its position_id instead of null.
  const isApplyingPosition =
    !!positionData?.isDisplayable &&
    !userData?.positions?.length &&
    !isStatusFailed(positionsApiStatus);

  useEffect(() => {
    if (!userData?.id || isApplyingPosition) return;

    setUser(userData.id);
    pushUserIdEvent(userData.id, positionId);

    const pending = consumePendingLoginEvent();

    if (pending) {
      // userType is known for email logins; for OAuth we approximate:
      // users who have never submitted a profile are treated as new.
      const userType =
        pending.userType ??
        (userData.profileSubmittedAt === null ? 'new' : 'existing');
      pushLoginEvent(pending.method, userType, positionId);
    }
  }, [userData?.id, positionId, isApplyingPosition]);

  useEffect(() => {
    if (!userData?.isEmailVerified || !userData?.profileSubmittedAt) return;

    const pending = consumePendingApplicationEvent();

    if (pending) {
      pushApplicationEvent(
        !!userData.closedAt,
        positionId ?? pending.positionId,
      );
    }
  }, [userData?.isEmailVerified, userData?.profileSubmittedAt]);
};
